import uiI18n from "../i18n";
import { auth } from "../firebase";
import { API_BASE_URL } from "../Variables";
import { retryAfterSeconds } from "../utils/retryAfter";

export class ApiError extends Error {
  constructor(status, message, code) {
    super(message);
    this.status = status;
    // Optional machine-readable error code of the API (e.g. "duplicate_proposal"), for translated messages.
    if (code) this.code = code;
  }
}

export async function getCurrentUser() {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) throw new ApiError(401, uiI18n.t("ui.sign_in_to_continue"));
  return user;
}

export function assertSession(uid) {
  if (auth.currentUser?.uid !== uid) throw new ApiError(401, uiI18n.t("ui.your_session_has_changed_sign_in_again"));
}

export async function getIdToken(forceRefresh = false) {
  const user = await getCurrentUser();
  const token = await user.getIdToken(forceRefresh);
  assertSession(user.uid);
  return token;
}

const messages = {
  get 400() { return uiI18n.t("ui.invalid_request_check_the_information_entered"); },
  get 401() { return uiI18n.t("ui.your_session_is_invalid_or_expired_sign_in_again"); },
  get 403() { return uiI18n.t("ui.you_are_not_allowed_to_perform_this_action"); },
  get 404() { return uiI18n.t("ui.this_resource_could_not_be_found_or_has_been_deleted"); },
  get 409() { return uiI18n.t("ui.this_action_conflicts_with_the_current_data_refresh_and_try_again"); },
  get 410() { return uiI18n.t("ui.this_listing_has_been_deleted"); },
};

export async function responseError(response) {
  let message = messages[response.status] || uiI18n.t("ui.server_unavailable");
  let code;
  if (response.status < 500) {
    const data = await response.clone().json().catch(() => null);
    if (typeof data?.error === "string" && /^[a-z_]{1,60}$/.test(data.error)) code = data.error;
    const candidate = data?.message || data?.Message;
    if (![401, 403].includes(response.status) && typeof candidate === "string" && candidate.length <= 300 &&
        !/[<>\r\n]|stack|exception| at |System\.|SELECT |INSERT |UPDATE /i.test(candidate)) message = candidate;
  }
  const error = new ApiError(response.status, message, code);
  // Rate limit: the wait announced by the API (readable only if CORS exposes Retry-After).
  const retryAfter = response.status === 429 ? retryAfterSeconds(response.headers?.get("Retry-After")) : null;
  if (retryAfter) error.retryAfter = retryAfter;
  return error;
}

export async function apiFetch(path, options = {}) {
  const { authenticated = true, ...init } = options;
  const url = new URL(path, API_BASE_URL);
  if (!url.href.startsWith(API_BASE_URL)) throw new Error(uiI18n.t("ui.api_url_not_allowed"));
  const headers = new Headers(init.headers);
  const user = authenticated ? await getCurrentUser() : null;
  if (user) {
    headers.set("Authorization", `Bearer ${await user.getIdToken()}`);
    assertSession(user.uid);
  }
  if (init.body instanceof FormData) headers.delete("Content-Type");
  let response;
  try {
    response = await fetch(url.href, { ...init, headers });
    if (response.status === 401 && user) {
      headers.set("Authorization", `Bearer ${await user.getIdToken(true)}`);
      assertSession(user.uid);
      response = await fetch(url.href, { ...init, headers });
    }
  } catch (error) {
    if (error.name === "AbortError" || error instanceof ApiError) throw error;
    throw new ApiError(0, uiI18n.t("ui.unable_to_connect_check_your_connection_and_try_again"));
  }
  if (user) assertSession(user.uid);
  if (!response.ok) throw await responseError(response);
  return response;
}

export async function readResponse(response) {
  if (response.status === 204) return null;
  const text = await response.text();
  if (!text.trim()) return null;
  try { return JSON.parse(text); } catch { return text; }
}

export async function apiRequest(path, options) {
  return readResponse(await apiFetch(path, options));
}

export function jsonOptions(method, body) {
  return { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
}
